import { Injectable } from '@angular/core';
import { TranslationService } from './translation.service';

export enum MessageType {
  ERROR = 'error',
  COUNTDOWN = 'countdown',
  INSTRUCTION = 'instruction'
}

export interface CameraMessage {
  text: string;
  type: MessageType;
}

interface MessageHistory {
  message: CameraMessage;
  count: number;
  priority: number;
}

@Injectable({
  providedIn: 'root'
})
export class MessageHandlerService {
  private readonly MESSAGE_STABILITY_FRAMES = 5;
  private messageHistory: MessageHistory = {
    message: { text: '', type: MessageType.INSTRUCTION },
    count: 0,
    priority: 0
  };
  private lastErrorMessage: CameraMessage = { text: '', type: MessageType.INSTRUCTION };
  private hasPositionError = false;

  constructor(private translationService: TranslationService) { }

  setHasPositionError(hasError: boolean): void {
    this.hasPositionError = hasError;
  }

  updateMessage(newMessage: string, priority: number = 1, type: MessageType = MessageType.ERROR): CameraMessage {
    const messageObj: CameraMessage = { text: newMessage, type };

    // If there's a position error and we're trying to show a countdown (lower priority),
    // don't update the message
    if (this.hasPositionError && priority < 2) {
      return this.lastErrorMessage;
    }

    // If it's the same message, just update it
    if (newMessage === this.messageHistory.message.text) {
      this.messageHistory.count++;
      this.lastErrorMessage = messageObj;
      return messageObj;
    }

    // If new message has lower priority than current one and current hasn't stabilized, keep current
    if (priority < this.messageHistory.priority && 
        this.messageHistory.count < this.MESSAGE_STABILITY_FRAMES) {
      this.messageHistory.count++;
      return this.lastErrorMessage;
    }

    // Switch to new message if it has higher/equal priority or current message is stable
    if (priority >= this.messageHistory.priority || 
        this.messageHistory.count >= this.MESSAGE_STABILITY_FRAMES) {
      this.messageHistory = { 
        message: messageObj, 
        count: 1,
        priority: priority 
      };
      this.lastErrorMessage = messageObj;
      return messageObj;
    }

    return this.lastErrorMessage;
  }

  getCountdownMessage(elapsedMs: number, requiredMs: number): CameraMessage {
    const remainingMs = requiredMs - elapsedMs;
    if (remainingMs > 0) {
      const secondsRemaining = Math.ceil(remainingMs / 1000);
      const text = this.translationService.translate('camera.capture.holdStill', { seconds: secondsRemaining });
      return { text, type: MessageType.COUNTDOWN };
    }
    return { text: this.translationService.translate('camera.capture.capturing'), type: MessageType.COUNTDOWN };
  }

  getMovementDetectedMessage(): CameraMessage {
    return { text: this.translationService.translate('camera.capture.movementDetected'), type: MessageType.ERROR };
  }
} 
